import { useEffect, useState } from 'react';
import { Col, Modal } from 'react-bootstrap';
import { FaShoppingCart } from 'react-icons/fa';

import CustomModal from '@/resources/components/global/CustomModal';
import Price from '@/resources/components/product/price/Price';
import { useWebContext } from '@/resources/hooks/useWebContext';
import { IBestCombo } from '@/store/ducks/combos/types';
import { ICartProduct } from '@/store/ducks/order/types';
import { IFullProduct, IProduct } from '@/store/ducks/products/types';

import {
  getDiscountPrice,
  getItemCountText,
  getQuantitySelectionText,
  submitComboPromotion,
} from '../../utils/comboUtils';
import { getQuantitiesOnEdit } from '../utils';
import { ComboFormVariants } from './ComboForm';

interface ComboModalProps {
  show: boolean;
  onClose: () => void;
  product: IProduct | { best_combo: IBestCombo } | undefined;
  variants: Record<string, IFullProduct[]>;
  editProducts?: IProduct[] | ICartProduct[];
}

export function ComboModal({ show, onClose, product, variants, editProducts }: ComboModalProps) {
  const { order } = useWebContext();
  const [quantities, setQuantities] = useState<Record<number, number>>({});

  useEffect(() => {
    if (editProducts) {
      getQuantitiesOnEdit(editProducts, setQuantities);
    } else if (product?.best_combo?.main_products) {
      const initialQuantities: Record<number, number> = {};
      product.best_combo.main_products.forEach(({ full_product, min_quantity }) => {
        const productVariants = variants[full_product.id];
        if (!productVariants?.length) {
          initialQuantities[full_product.id] = min_quantity ?? 0;
        } else {
          initialQuantities[full_product.id] = 0;
          const firstVariant = productVariants[0];
          productVariants.forEach((variant) => {
            initialQuantities[variant.id] = variant.id === firstVariant?.id ? min_quantity ?? 0 : 0;
          });
        }
      });
      setQuantities(initialQuantities);
    }
  }, [editProducts, product, variants]);

  const products =
    product?.best_combo?.main_products.map(
      ({ full_product, discount_percentage, min_quantity, max_quantity }) => ({
        product: {
          ...full_product,
          ...full_product.product,
          title:
            full_product.product?.full_product?.title || full_product.product?.title || 'Product',
          min_quantity,
          max_quantity,
        },
        discount_percentage,
      }),
    ) || [];

  const getTotalQuantityForProduct = (productId: number): number => {
    const productVariants = variants[productId];
    if (!productVariants?.length) {
      return quantities[productId] ?? 0;
    }

    return productVariants.reduce((total, variant) => {
      return total + (quantities[variant.id] ?? 0);
    }, 0);
  };

  const handleSubmitComboPromotion = async (): Promise<void> => {
    const result = await submitComboPromotion({
      quantities,
      combo_seq_id: editProducts?.[0]?.combo_seq_id,
      comboId: editProducts ? editProducts?.[0]?.combo_id || 0 : product?.best_combo?.id || 0,
      onClose,
      orderId: order?.id,
    });

    if (!result.success) {
      console.error('Combo promotion failed:', result.error);
    }
  };

  const isValidCombo = (): boolean => {
    return products.every(({ product }) => {
      const totalQuantity = getTotalQuantityForProduct(product.id);
      return totalQuantity >= (product.min_quantity || 0);
    });
  };

  const getTotalItemsSelected = (): number => {
    return Object.values(quantities).reduce((total, quantity) => total + quantity, 0);
  };

  const calculateTotalPrice = (): number => {
    let total = 0;

    products.forEach(({ product, discount_percentage }) => {
      const discountedPrice = getDiscountPrice(product.price, discount_percentage);

      if (!variants[product.id]?.length) {
        const quantity = quantities[product.id] ?? product.min_quantity;
        total += discountedPrice * quantity;
      } else {
        const productVariants = variants[product.id];
        if (productVariants) {
          productVariants.forEach((variant) => {
            const quantity = quantities[variant.id] ?? 0;
            const variantDiscountedPrice = getDiscountPrice(variant.price, discount_percentage);
            total += variantDiscountedPrice * quantity;
          });
        }
      }
    });

    return total;
  };

  const canAddQuantity = (productId: number, variantId: number, newQuantity: number): boolean => {
    const productConfig = products.find((p) => p.product.id === productId);
    if (!productConfig) return false;

    const maxAllowed = productConfig.product.max_quantity;
    const currentTotal = getTotalQuantityForProduct(productId);
    const currentVariantQuantity = quantities[variantId] ?? 0;
    const newTotal = currentTotal - currentVariantQuantity + newQuantity;

    return newTotal <= maxAllowed;
  };

  return (
    <CustomModal show={show} onHide={onClose} centered className="combo-modal">
      <div className="d-flex flex-column">
        <Modal.Header className="d-flex flex-column">
          <span className="form-title">Personalize seu combo</span>
          <span className="form-subtitle">Combo {product?.best_combo?.name}</span>
        </Modal.Header>

        <div className="d-flex flex-column align-items-center justify-content-center">
          {products.map(({ product, discount_percentage }) => (
            <div
              key={product.id}
              className="w-100"
              style={{
                maxHeight: '26.563rem',
                overflow: 'auto',
              }}
            >
              <Col
                xs={12}
                className="combo-product-card d-flex flex-column mb-0 pl-3"
                style={{ backgroundColor: '#0000000D' }}
              >
                <span>{product.title}</span>
                <span className="text-muted" style={{ fontSize: '0.831rem' }}>
                  {getQuantitySelectionText(product, variants, getTotalQuantityForProduct)}
                </span>
              </Col>

              {!variants[product.id]?.length ? (
                <ComboFormVariants
                  key={product.id}
                  product={product}
                  discount_percentage={discount_percentage}
                  maxQuantity={product.max_quantity}
                  min_quantity={product.min_quantity}
                  currentQuantity={quantities[product.id] ?? product.min_quantity}
                  onQuantityChange={(newQuantity: number) => {
                    if (canAddQuantity(product.id, product.id, newQuantity)) {
                      setQuantities((prev) => ({
                        ...prev,
                        [product.id]: newQuantity,
                      }));
                    }
                  }}
                />
              ) : (
                variants[product.id]?.map((variant) => {
                  const currentVariantQuantity = quantities[variant.id] ?? 0;
                  const totalUsedByOtherVariants =
                    getTotalQuantityForProduct(product.id) - currentVariantQuantity;
                  const availableForThisVariant = product.max_quantity - totalUsedByOtherVariants;

                  return (
                    <ComboFormVariants
                      key={variant.id}
                      product={variant}
                      maxQuantity={Math.max(0, availableForThisVariant)}
                      min_quantity={0}
                      discount_percentage={discount_percentage}
                      currentQuantity={quantities[variant.id] ?? 0}
                      onQuantityChange={(newQuantity: number) => {
                        if (canAddQuantity(product.id, variant.id, newQuantity)) {
                          setQuantities((prev) => ({
                            ...prev,
                            [variant.id]: newQuantity,
                          }));
                        }
                      }}
                    />
                  );
                })
              )}
            </div>
          ))}
        </div>
        <div className="border-top p-3 mt-0">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <span className="fw-bold">Total ({getItemCountText(getTotalItemsSelected())}):</span>
            </div>
            <div>
              <Price defaultPrice={calculateTotalPrice()} price={calculateTotalPrice()} />
            </div>
          </div>

          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary flex-fill" onClick={onClose}>
              Cancelar
            </button>
            <button
              className="btn btn-primary flex-fill d-flex align-items-center justify-content-center w-25"
              onClick={handleSubmitComboPromotion}
              disabled={getTotalItemsSelected() === 0 || !isValidCombo()}
            >
              <FaShoppingCart data-testid="cart-icon" className="mr-2" color="white" />
              Adicionar ao carrinho
            </button>
          </div>
        </div>
      </div>
    </CustomModal>
  );
}
